import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { message } from 'antd'
import io from 'socket.io-client'
import People from './people'
import { apiBaseURL, socketURL } from '../config'

export default function Home() {
  const senderId = localStorage.getItem('uuid')
  const [contacts, setContacts] = useState([])
  const [activeContactId, setActiveContactId] = useState('')
  const [enlargedImage, setEnlargedImage] = useState(null)
  const currentContact = contacts.find(contact => contact.id === activeContactId) || {}
  const [socket, setSocket] = useState(null)
  const messagesEndRef = useRef(null)

  const fetchContacts = () => {
    axios.post('/c/kf_b/contact/list/get')
      .then(function (response) {
        if (response.errcode === 0) {
          const sortedContacts = response.data.sort((a, b) => {
            const lastMessageA = a.messages[a.messages.length - 1]?.time
            const lastMessageB = b.messages[b.messages.length - 1]?.time

            if (!lastMessageA || isNaN(new Date(lastMessageA).getTime())) return 1
            if (!lastMessageB || isNaN(new Date(lastMessageB).getTime())) return -1

            return new Date(lastMessageB).getTime() - new Date(lastMessageA).getTime()
          })
          setContacts(sortedContacts)
        } else {
          message.warning(response.msg)
        }
      })
      .catch(function (error) {
        console.error('请求失败，错误信息为：', error)
      })
  }

  useEffect(() => {
    fetchContacts()
  }, [])

  useEffect(() => {
    const socketConnection = io(socketURL, {
      query: {
        identity: senderId,
      },
    })
    setSocket(socketConnection)

    socketConnection.on('json', (newMessage) => {
      const contactId = newMessage.senderId === senderId ? newMessage.receiverId : newMessage.senderId
    
      setContacts((prevContacts) => {
        // 检查联系人是否已经存在
        const contactExists = prevContacts.some(contact => contact.id === contactId)
    
        // 如果联系人不存在，添加新联系人
        if (!contactExists) {
          const newContact = {
            id: contactId,
            name: newMessage.senderName || '新联系人',
            avatar: newMessage.senderAvatar || '/images/avatar.png',
            messages: [{ ...newMessage, sender: 'received' }],
            unread_quantity: 1,
          }
          return [newContact, ...prevContacts]
        }
    
        // 更新联系人消息列表和未读数量
        const updatedContacts = prevContacts.map((contact) => {
          if (contact.id === contactId) {
            // 如果是当前联系人，直接添加到消息列表
            if (contact.id === activeContactId) {
              return {
                ...contact,
                messages: [...contact.messages, { ...newMessage, sender: 'received' }],
                unread_quantity: 0, // 当前联系人未读数量清零
              }
            } else {
              // 如果不是当前联系人，增加未读数量
              return {
                ...contact,
                messages: [...contact.messages, { ...newMessage, sender: 'received' }],
                unread_quantity: (contact.unread_quantity || 0) + 1, // 增加未读数量
              }
            }
          }
          return contact
        })
    
        // 按最新消息时间重新排序
        return updatedContacts.sort((a, b) => {
          const lastMessageA = a.messages[a.messages.length - 1]?.time
          const lastMessageB = b.messages[b.messages.length - 1]?.time
    
          // 如果时间无效，则将该联系人排到最后
          if (!lastMessageA || isNaN(new Date(lastMessageA).getTime())) return 1
          if (!lastMessageB || isNaN(new Date(lastMessageB).getTime())) return -1
    
          return new Date(lastMessageB).getTime() - new Date(lastMessageA).getTime()
        })
      })
    })

    return () => {
      if (socketConnection) {
        socketConnection.disconnect()
      }
    }
  }, [senderId, activeContactId]) // 添加 activeContactId 作为依赖

  // 监听消息列表的变化，滚动到底部
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
    }
  }, [currentContact?.messages]) // 监听消息列表的变化

  // 发送消息
  const handleSendMessage = (content, type = 'text') => {
    if (!content.trim() && type === 'text') return

    const newMessage = {
      content: content,
      sender: 'sent',
      senderId: senderId,
      senderRole: 'teacher',
      receiverId: activeContactId,
      receiverRole: 'student',
      type,
      time: new Date().toISOString()
    }

    setContacts((prevContacts) => {
      // 更新当前联系人的消息列表
      const updatedContacts = prevContacts.map((contact) => {
        if (contact.id === activeContactId) {
          return {
            ...contact,
            messages: [...contact.messages, newMessage],
          }
        }
        return contact
      })

      // 按最新消息时间重新排序
      return updatedContacts.sort((a, b) => {
        const lastMessageA = a.messages[a.messages.length - 1]?.time
        const lastMessageB = b.messages[b.messages.length - 1]?.time

        // 如果时间无效，则将该联系人排到最后
        if (!lastMessageA || isNaN(new Date(lastMessageA).getTime())) return 1
        if (!lastMessageB || isNaN(new Date(lastMessageB).getTime())) return -1

        return new Date(lastMessageB).getTime() - new Date(lastMessageA).getTime()
      })
    })

    if (socket) {
      socket.emit('json', newMessage)
    }
  }

  // 加载历史消息
  const loadMessages = (contactId) => {
    axios.post('/c/kf_b/chat_history/get', {
      contact_id: contactId,
    })
      .then(function (response) {
        if (response.errcode === 0) {
          setContacts((prevContacts) => {
            return prevContacts.map((contact) => {
              if (contact.id === contactId) {
                return {
                  ...contact,
                  messages: response.data.records,
                  unread_quantity: 0, // 清零未读数量
                }
              }
              return contact
            })
          })
        } else {
          message.warning(response.msg)
        }
      })
      .catch(function (error) {
        console.error('请求失败，错误信息为：', error)
      })
  }

  // 上传图片
  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    const formData = new FormData()
    formData.append('image', file)

    if (file) {
      axios.post(`${apiBaseURL}/common/image/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then(function (response) {
          if (response.errcode === 0) {
            handleSendMessage(response.data.image_url, 'image')
          } else {
            message.warning(response.data.msg)
          }
        })
        .catch(function (error) {
          console.error('请求失败，错误信息为：', error)
        })
    }
  }

  // 点击放大图片
  const handleImageClick = (imageSrc) => {
    setEnlargedImage(imageSrc)
  }

  // 关闭放大图片
  const handleCloseEnlargedImage = () => {
    setEnlargedImage(null)
  }

  // 友好时间
  const formatMessageTime = (timestamp) => {
    if (!timestamp) {
      return '未知时间' // 如果时间无效，返回默认值
    }

    const now = new Date()
    const messageDate = new Date(timestamp)

    // 检查 messageDate 是否是有效的时间
    if (isNaN(messageDate.getTime())) {
      return '未知时间' // 如果时间无效，返回默认值
    }

    // 判断是否是今天
    const isToday = now.toDateString() === messageDate.toDateString()
    // 判断是否是昨天
    const isYesterday = new Date(now.setDate(now.getDate() - 1)).toDateString() === messageDate.toDateString()
    // 判断是否是今年
    const isThisYear = now.getFullYear() === messageDate.getFullYear()

    if (isToday) {
      // 今天：显示时间
      return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (isYesterday) {
      // 昨天：显示“昨天 + 时间”
      return `昨天 ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    } else if (isThisYear) {
      // 今年：显示月日和时间
      return `${messageDate.getMonth() + 1}-${messageDate.getDate()} ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    } else {
      // 不是今年：显示年月日和时间
      return `${messageDate.getFullYear()}-${messageDate.getMonth() + 1}-${messageDate.getDate()} ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    }
  }

  return (
    <div className="home-page">
      <div className="home-top">
        <div className="home-topBox">
          <div className="home-logo">
            <img src="/images/logo.jpg" alt="" />
            <p>云招客服</p>
          </div>
          <People />
        </div>
      </div>

      <div className="home-main">
        <div className="home-main-left">
          <div className="home-contact-list">
            <div className="home-contact-header">联系人</div>
            {contacts.map(contact => (
              <div
                key={contact.id}
                className={`home-contact-item ${activeContactId === contact.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveContactId(contact.id)
                  loadMessages(contact.id)
                }}
              >
                <div className="home-contact-avatar">
                  <img src={contact.avatar || '/images/avatar.png'} alt="avatar" />
                  {contact.unread_quantity > 0 && (
                    <span className="home-unread-badge">{contact.unread_quantity}</span>
                  )}
                </div>
                <span>{contact.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="home-main-right">
          {currentContact?.id && (
            <div className="home-chat-window">
              <div className="home-chat-header">与 {currentContact?.name} 的对话</div>

              <div className="home-chat-messages">
                {currentContact?.messages.map((message, index) => {
                  const currentTime = new Date(message.time).getTime()
                  const prevTime = index > 0 ? new Date(currentContact.messages[index - 1].time).getTime() : null
                  const showTime = !prevTime || (currentTime - prevTime) > 5 * 60 * 1000

                  return (
                    <React.Fragment key={index}>
                      {showTime && (
                        <div className="home-message-time">
                          {formatMessageTime(message.time)}
                        </div>
                      )}

                      {message.type === 'text' ? (
                        <div className={`home-message ${message.sender}`}>
                          <span>{message.content}</span>
                        </div>
                      ) : (
                        <img
                          src={message.content}
                          alt="sent"
                          className={`home-chat-image ${message.sender === 'sent' ? 'home-sent-image' : 'home-received-image'}`}
                          onClick={() => handleImageClick(message.content)}
                        />
                      )}
                    </React.Fragment>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className="home-chat-input">
                <div className="home-chat-input--upload">
                  <label htmlFor="file-upload">
                    <img src="/images/upload.png" alt="上传图片" />
                    <input
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                    />
                  </label>
                </div>

                <textarea
                  className="home-chat-input-textarea"
                  placeholder=""
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage(e.target.value)
                      e.target.value = ''
                    }
                  }}
                />

                <div className="home-chat-input-send">
                  <button onClick={() => {
                    const input = document.querySelector('.home-chat-input-textarea')
                    handleSendMessage(input.value)
                    input.value = ''
                  }}>
                    发送
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {enlargedImage && (
        <div className="home-image-modal" onClick={handleCloseEnlargedImage}>
          <img src={enlargedImage} alt="enlarged" className="home-enlarged-image" />
        </div>
      )}
    </div>
  )
}
